import { Fragment, useState } from "react";
import Switch from "@mui/material/Switch";
import { AdvancedFilters } from "../filters/AdvancedFilters";
import { cloneFilter, type AdvancedFilter } from "../filters/filter-logic";
import { MyFilters } from "../filters/MyFilters";
import { PSidebar } from "../ui/PSidebar";
import { PSelectButton } from "../ui/PSelectButton";
import { QUICK_FILTERS, type QuickFilterCode } from "./quick-filters";

export type FilterTab = "ft_quick_filters" | "ft_advanced_filters" | "ft_my_filters";

// THE IN PLAY FILTER SIDEBAR (`in-play.component.html`): NON-MODAL, LEFT,
// `w-full md:w-30rem`. A `p-fluid text-sm` SELECT BUTTON (QUICK / ADVANCED / MY FILTERS), THEN:
// - QUICK FILTERS: THE `card mt-3`, ONE ROW PER PRESET THE USER'S ROLES ALLOW (LABEL LEFT,
//   SWITCH RIGHT) AND A `p-divider` AFTER EVERY PRESET - ALSO THE HIDDEN ONES, AS IN ANGULAR,
//   WHICH IS WHY HIDDEN PRESETS STILL ADD SPACE;
// - ADVANCED FILTERS: `advanced-filters`, EDITING A DRAFT OF THE CURRENT FILTER;
// - MY FILTERS: `my-filters`, THE USER'S SAVED FILTERS.
// THE TAB IS THE PAGE'S (ANGULAR'S `selected_filter_type`), SO IT SURVIVES CLOSING THE SIDEBAR.
export function FilterSidebar(props: {
  open: boolean;
  onClose: () => void;
  tab: FilterTab;
  onTab: (t: FilterTab) => void;
  allowed: ReadonlySet<QuickFilterCode>;
  active: QuickFilterCode | null;
  onSelect: (code: QuickFilterCode) => void;
  // WHAT THE ADVANCED TAB STARTS FROM: THE APPLIED ADVANCED FILTER, OR AN ALL-OFF ONE
  current: AdvancedFilter;
  onApplyAdvanced: (f: AdvancedFilter) => void;
}) {
  const { open, onClose, tab, onTab, allowed, active, onSelect, current, onApplyAdvanced } = props;

  // THE DRAFT IS RE-SEEDED FROM WHAT IS APPLIED EACH TIME THE SIDEBAR OPENS, SO AN UNAPPLIED EDIT
  // DOES NOT LINGER (ANGULAR EDITS THE APPLIED FILTER OBJECT ITSELF), AND IT SURVIVES A TAB SWITCH
  const [draft, setDraft] = useState<AdvancedFilter>(() => cloneFilter(current));
  const [seeded, setSeeded] = useState(open);
  if (open && !seeded) {
    setSeeded(true);
    setDraft(cloneFilter(current));
  } else if (!open && seeded) {
    setSeeded(false);
  }

  return (
    <PSidebar open={open} onClose={onClose} position="left" modal={false} width={{ xs: "100%", md: "30rem" }} ariaLabel="Filters">
      <PSelectButton
        className="text-sm"
        ariaLabel="Filter type"
        value={tab}
        onChange={onTab}
        options={[
          { label: "Quick Filters", value: "ft_quick_filters" },
          { label: "Advanced Filters", value: "ft_advanced_filters" },
          { label: "My Filters", value: "ft_my_filters" },
        ]}
      />
      {tab === "ft_quick_filters" && (
        <div className="card mt-3 fadein animation-duration-1000">
          {QUICK_FILTERS.map((qf) => (
            <Fragment key={qf.code}>
              {allowed.has(qf.code) && (
                <div
                  className="flex justify-content-between align-items-center cursor-pointer"
                  onClick={() => {
                    onSelect(qf.code);
                    onClose();
                  }}
                >
                  <div>{qf.label}</div>
                  <Switch
                    checked={active === qf.code}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => {
                      onSelect(qf.code);
                      onClose();
                    }}
                    slotProps={{ input: { "aria-label": qf.label } }}
                  />
                </div>
              )}
              {/* `p-divider`: 1PX `--surface-border` LINE WITH 1REM VERTICAL MARGIN */}
              <div role="separator" style={{ margin: "1rem 0", borderTop: "1px solid var(--surface-border)" }} />
            </Fragment>
          ))}
        </div>
      )}
      {tab === "ft_advanced_filters" && (
        <div className="fadein animation-duration-1000">
          <AdvancedFilters filter={draft} onChange={setDraft} onApply={onApplyAdvanced} />
        </div>
      )}
      {tab === "ft_my_filters" && (
        <div className="fadein animation-duration-1000">
          <MyFilters onApply={onApplyAdvanced} />
        </div>
      )}
    </PSidebar>
  );
}
